import React, { useCallback, useState } from 'react';
import { Chat, PromptVariable } from '../types';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { useChatContext } from '../chat/chatContext';
import { LibraryTab } from '../library/containers/LibraryTab';
import { growegyAIConfig } from '../../commons/config';
import useAnalyticsPage from '../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../web-analytics/Page';
import { ChatTab } from '../chat/containers/ChatTab';
import { VariablesTab } from '../variables/containers/VariablesTab';
import { CHAT_TYPE_GENERAL } from '../dto';
import { ChatRealmContextProvider } from '../chat/chatRealmContext';
import { usePromptClickedCallback } from '../chat/hooks/usePromptClickedCallback';

const CHAT_TAB_INDEX = 0;
const LIBRARY_TAB_INDEX = 1;
const VARIABLES_TAB_INDEX = 2;

export const GrowegyAI = () => (
  <ChatRealmContextProvider chatType={CHAT_TYPE_GENERAL}>
    <GrowegyAIInternal />
  </ChatRealmContextProvider>
);

const GrowegyAIInternal = () => {
  useAnalyticsPage(Page.GROWEGY_AI);
  const {
    getCurrentChat,
    setCurrentChat,
    setSelectedCategory,
    chatTypeAndSubtype: { chatType, chatSubtype },
  } = useChatContext();
  const [selectedChat, setSelectedChat] = useState<Chat | null>(getCurrentChat());

  const onPromptClicked = usePromptClickedCallback(chatType, chatSubtype, (chat) => {
    setSelectedChat(chat);
    setSelectedTabIndex(CHAT_TAB_INDEX);
  });
  const onOpenLibraryClick = useCallback(() => {
    setSelectedCategory(null);
    setSelectedTabIndex(LIBRARY_TAB_INDEX);
  }, [setSelectedCategory]);

  const onOpenVariablesClick = useCallback(() => {
    setSelectedCategory(null);
    setSelectedTabIndex(VARIABLES_TAB_INDEX);
  }, [setSelectedCategory]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(CHAT_TAB_INDEX);

  const onChatSelected = useCallback(
    (chat: Chat) => {
      setSelectedChat(chat);
      setCurrentChat(chat);
    },
    [setCurrentChat]
  );

  const [selectedVariable, setSelectedVariable] = useState<PromptVariable | null>(null);

  return (
    <div className="growegy-ai__container">
      <Tabs selectedIndex={selectedTabIndex} onSelect={(i) => setSelectedTabIndex(i)}>
        <TabList>
          <Tab data-test="growegy-ai__chat-tab">Chat</Tab>
          {growegyAIConfig.libraryEnabled && <Tab data-test="growegy-ai__library-tab">Library</Tab>}
          {growegyAIConfig.variablesEnabled && (
            <Tab data-test="growegy-ai__variables-tab">Personalization</Tab>
          )}
        </TabList>
        <TabPanel>
          <div>
            <ChatTab
              selectedChat={selectedChat}
              onChatSelected={onChatSelected}
              onOpenLibraryClick={onOpenLibraryClick}
              onOpenVariablesClick={onOpenVariablesClick}
              onPromptClick={onPromptClicked}
            />
          </div>
        </TabPanel>
        {growegyAIConfig.variablesEnabled && (
          <TabPanel>
            <div>
              <LibraryTab onPromptClicked={onPromptClicked} />
            </div>
          </TabPanel>
        )}
        <TabPanel>
          <div>
            <VariablesTab
              selectedVariable={selectedVariable}
              onVariableSelected={setSelectedVariable}
            />
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
};
